import { SplitDecision, TreePath } from './types';

/**
 * Extract paths from an Issue Flagger decision tree
 */
export const extractIssueFlaggerPaths = (
  node: any,
  currentPath: (SplitDecision | null)[] = [],
  maxDepth: number = 10
): TreePath<any>[] => {
  if (currentPath.length >= maxDepth) {
    return [];
  }

  if (node.type === 'issueFlaggerLeaf') {
    return [{
      decisions: [...currentPath],
      output: node.output
    }];
  }

  const paths: TreePath<any>[] = [];

  switch (node.type) {
    case 'numericalSplit':
      // Add paths for each range
      node.split.ranges.forEach((range: any) => {
        const newPath = [...currentPath];
        // Ensure the path array is large enough for the columnIndex
        while (newPath.length <= node.split.columnIndex) {
          newPath.push(null);
        }
        newPath[node.split.columnIndex] = { type: 'numerical', range: { maxValue: range.maxValue } };
        paths.push(...extractIssueFlaggerPaths(range.childNode, newPath, maxDepth));
      });
      // Add default path
      const defaultPath = [...currentPath];
      while (defaultPath.length <= node.split.columnIndex) {
        defaultPath.push(null);
      }
      defaultPath[node.split.columnIndex] = { type: 'numerical', range: 'default' };
      paths.push(...extractIssueFlaggerPaths(node.split.defaultChild, defaultPath, maxDepth));
      break;

    case 'categoricalSplit':
      // Add paths for each category
      node.split.categoryMap.forEach((category: any) => {
        const newPath = [...currentPath];
        while (newPath.length <= node.split.columnIndex) {
          newPath.push(null);
        }
        newPath[node.split.columnIndex] = { type: 'categorical', category: category.category };
        paths.push(...extractIssueFlaggerPaths(category.childNode, newPath, maxDepth));
      });
      // Add default path
      const defaultPathCategorical = [...currentPath];
      while (defaultPathCategorical.length <= node.split.columnIndex) {
        defaultPathCategorical.push(null);
      }
      defaultPathCategorical[node.split.columnIndex] = { type: 'categorical', category: 'default' };
      paths.push(...extractIssueFlaggerPaths(node.split.defaultChild, defaultPathCategorical, maxDepth));
      break;

    case 'booleanSplit':
      // Add true path if it exists
      if (node.split.trueChild) {
        const truePath = [...currentPath];
        while (truePath.length <= node.split.columnIndex) {
          truePath.push(null);
        }
        truePath[node.split.columnIndex] = { type: 'boolean', value: true };
        paths.push(...extractIssueFlaggerPaths(node.split.trueChild, truePath, maxDepth));
      }
      // Add false path
      const falsePath = [...currentPath];
      while (falsePath.length <= node.split.columnIndex) {
        falsePath.push(null);
      }
      falsePath[node.split.columnIndex] = { type: 'boolean', value: false };
      paths.push(...extractIssueFlaggerPaths(node.split.falseChild, falsePath, maxDepth));
      break;
  }

  return paths;
};

/**
 * Extract paths from a Strategy Recommendation decision tree
 */
export const extractStrategyRecommendationPaths = (
  node: any,
  currentPath: (SplitDecision | null)[] = [],
  maxDepth: number = 10
): TreePath<any>[] => {
  if (currentPath.length >= maxDepth) {
    return [];
  }

  if (node.type === 'strategyRecommendationLeaf') {
    return [{
      decisions: [...currentPath],
      output: node.output
    }];
  }

  const paths: TreePath<any>[] = [];

  switch (node.type) {
    case 'numericalSplit':
      // Add paths for each range
      node.split.ranges.forEach((range: any) => {
        const newPath = [...currentPath];
        while (newPath.length <= node.split.columnIndex) {
          newPath.push(null);
        }
        newPath[node.split.columnIndex] = { type: 'numerical', range: { maxValue: range.maxValue } };
        paths.push(...extractStrategyRecommendationPaths(range.childNode, newPath, maxDepth));
      });
      // Add default path
      const defaultPath = [...currentPath];
      while (defaultPath.length <= node.split.columnIndex) {
        defaultPath.push(null);
      }
      defaultPath[node.split.columnIndex] = { type: 'numerical', range: 'default' };
      paths.push(...extractStrategyRecommendationPaths(node.split.defaultChild, defaultPath, maxDepth));
      break;

    case 'categoricalSplit':
      // Add paths for each category
      node.split.categoryMap.forEach((category: any) => {
        const newPath = [...currentPath];
        while (newPath.length <= node.split.columnIndex) {
          newPath.push(null);
        }
        newPath[node.split.columnIndex] = { type: 'categorical', category: category.category };
        paths.push(...extractStrategyRecommendationPaths(category.childNode, newPath, maxDepth));
      });
      // Add default path
      const defaultPathCategorical = [...currentPath];
      while (defaultPathCategorical.length <= node.split.columnIndex) {
        defaultPathCategorical.push(null);
      }
      defaultPathCategorical[node.split.columnIndex] = { type: 'categorical', category: 'default' };
      paths.push(...extractStrategyRecommendationPaths(node.split.defaultChild, defaultPathCategorical, maxDepth));
      break;

    case 'booleanSplit':
      // Add true path if it exists
      if (node.split.trueChild) {
        const truePath = [...currentPath];
        while (truePath.length <= node.split.columnIndex) {
          truePath.push(null);
        }
        truePath[node.split.columnIndex] = { type: 'boolean', value: true };
        paths.push(...extractStrategyRecommendationPaths(node.split.trueChild, truePath, maxDepth));
      }
      // Add false path
      const falsePath = [...currentPath];
      while (falsePath.length <= node.split.columnIndex) {
        falsePath.push(null);
      }
      falsePath[node.split.columnIndex] = { type: 'boolean', value: false };
      paths.push(...extractStrategyRecommendationPaths(node.split.falseChild, falsePath, maxDepth));
      break;
  }

  return paths;
};

/**
 * Helper function to pad paths to match column count
 */
export const padPathsToColumnCount = <T>(
  paths: TreePath<T>[], 
  columnCount: number
): TreePath<T>[] => {
  return paths.map(path => ({
    ...path,
    decisions: [
      ...path.decisions,
      ...new Array(Math.max(0, columnCount - path.decisions.length)).fill(null)
    ]
  }));
};
